import mixpanel from 'mixpanel-browser';
import * as Sentry from '@sentry/nextjs';
import { 
  trackEvent as posthogTrackEvent, 
  trackError as posthogTrackError,
  setUserProperties as posthogSetUserProperties,
  identifyUser as posthogIdentifyUser
} from './posthog';

// Initialize Mixpanel
if (process.env.NEXT_PUBLIC_MIXPANEL_TOKEN) {
  mixpanel.init(process.env.NEXT_PUBLIC_MIXPANEL_TOKEN, {
    debug: process.env.NODE_ENV === 'development',
    track_pageview: true,
  });
}

// Sentry is now initialized via sentry.client.config.js and sentry.server.config.js
// This ensures proper configuration and avoids double initialization

// Unified tracking function that sends to both PostHog and legacy analytics
export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  try {
    // Track with PostHog (primary)
    posthogTrackEvent(eventName, properties);
    
    // Also track with Mixpanel if configured (for migration period)
    if (process.env.NEXT_PUBLIC_MIXPANEL_TOKEN) {
      mixpanel.track(eventName, properties);
    }
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};

// Unified error tracking with enhanced Sentry context
export const trackError = (error: Error, context?: Record<string, any>) => {
  try {
    // Track with PostHog
    posthogTrackError(error, context);
    
    // Track with Sentry with enhanced context
    if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
      Sentry.withScope((scope) => {
        // Add context as extra data
        if (context) {
          Object.keys(context).forEach(key => {
            scope.setExtra(key, context[key]);
          });
        }
        
        // Add tags for better filtering
        if (context?.event_id) {
          scope.setTag('event_id', context.event_id);
        }
        if (context?.invite_id) {
          scope.setTag('invite_id', context.invite_id);
        }
        if (context?.user_role) {
          scope.setTag('user_role', context.user_role);
        }
        if (context?.error_boundary) {
          scope.setTag('error_source', 'error_boundary');
        }
        
        // Set error level based on context
        if (context?.severity) {
          scope.setLevel(context.severity);
        } else if (error.name === 'NetworkError') {
          scope.setLevel('warning');
        } else {
          scope.setLevel('error');
        }
        
        // Add fingerprint for better grouping
        if (context?.component_stack) {
          scope.setFingerprint([error.name, error.message, context.component_stack]);
        }
        
        Sentry.captureException(error);
      });
    }
  } catch (err) {
    console.error('Error tracking error:', err);
  }
};

// Unified user properties with Sentry user context
export const setUserProperties = (properties: Record<string, any>) => {
  try {
    // Set with PostHog
    posthogSetUserProperties(properties);
    
    // Set with Sentry user context
    if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
      Sentry.setUser({
        id: properties.user_id || properties.id,
        email: properties.email,
        username: properties.username || `${properties.firstName} ${properties.lastName}`.trim(),
        ...properties
      });
    }
    
    // Also set with Mixpanel if configured
    if (process.env.NEXT_PUBLIC_MIXPANEL_TOKEN) {
      mixpanel.people.set(properties);
    }
  } catch (error) {
    console.error('Error setting user properties:', error);
  }
};

// User identification
export const identifyUser = (userId: string, properties?: Record<string, any>) => {
  try {
    // Identify with PostHog
    posthogIdentifyUser(userId, properties);
    
    // Set user context in Sentry
    if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
      Sentry.setUser({
        id: userId,
        ...properties
      });
      
      // Set additional context
      Sentry.setTag('user_identified', 'true');
      if (properties?.user_role) {
        Sentry.setTag('user_role', properties.user_role);
      }
    }
    
    // Also identify with Mixpanel if configured
    if (process.env.NEXT_PUBLIC_MIXPANEL_TOKEN) {
      mixpanel.identify(userId);
      if (properties) {
        mixpanel.people.set(properties);
      }
    }
  } catch (error) {
    console.error('Error identifying user:', error);
  }
};

// Re-export PostHog specific functions
export { 
  trackPageView,
  getFeatureFlag,
  isFeatureEnabled,
  startSessionRecording,
  stopSessionRecording,
  showSurvey,
  setGroup,
  trackInviteEvent,
  trackEventAction,
  trackRSVPAction,
  trackPerformance,
  trackUserJourney,
  trackInvalidLink
} from './posthog';

// Legacy support
export const trackMixpanelEvent = (eventName: string, properties?: Record<string, any>) => {
  if (process.env.NEXT_PUBLIC_MIXPANEL_TOKEN) {
    mixpanel.track(eventName, properties);
  }
};

export const trackSentryError = (error: Error, context?: Record<string, any>) => {
  if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
    Sentry.captureException(error, { extra: context });
  }
};

// Additional Sentry utilities
export const setSentryContext = (context: Record<string, any>) => {
  if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
    Sentry.setContext('app_context', context);
  }
};

export const addSentryBreadcrumb = (message: string, category?: string, level?: 'info' | 'warning' | 'error') => {
  if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
    Sentry.addBreadcrumb({
      message,
      category: category || 'app',
      level: level || 'info',
      timestamp: Date.now() / 1000,
    });
  }
};

export const startSentryTransaction = (name: string, op?: string) => {
  if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
    return Sentry.startTransaction({
      name,
      op: op || 'navigation',
    });
  }
  return null;
};

export const setSentryTag = (key: string, value: string) => {
  if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
    Sentry.setTag(key, value);
  }
};